import React, { useEffect, useState } from "react";
import { getStripeProducts } from "../../utils/billing";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const formatAmount = (amount?: number | null, currency?: string) => {
  if (typeof amount !== "number") return "-";

  const normalizedCurrency = (currency || "usd").toUpperCase();

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency,
  }).format(amount / 100);
};

const getProductName = (product: any) =>
  product.name || product.metadata?.name || product.id || "-";

const getPriceName = (price: any) =>
  price.nickname || price.metadata?.name || price.id || "-";

const getProductPrices = (product: any) => {
  if (Array.isArray(product.prices)) return product.prices;
  if (Array.isArray(product.priceList)) return product.priceList;
  if (Array.isArray(product.priceData)) return product.priceData;

  return [];
};

export default function StripeProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getStripeProducts({ type: "all" });
        setProducts([...(response.data || [])].reverse());
      } catch (error) {
        console.error("Error fetching stripe products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#211f26]">
          Stripe Products
        </h1>
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        {loading ? (
          <div className="text-[14px] text-[#84828e]">Loading...</div>
        ) : (
          <>
            <Accordion type="multiple" className="w-full">
              {products.map((product: any, index: number) => {
                const prices = getProductPrices(product);
                const productId = product.id || product.productId || "-";

                return (
                  <AccordionItem
                    key={product.id || product._id || index}
                    value={product.id || product._id || `product-${index}`}
                    className="border-[#eae7ec]"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="grid w-full grid-cols-[56px_minmax(180px,1.4fr)_minmax(180px,1fr)_96px_96px] items-center gap-4 pr-3 text-left">
                        <span className="text-[13px] text-[#84828e]">
                          #{index + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-[15px] font-semibold text-[#211f26]">
                            {getProductName(product)}
                          </span>
                          <span className="block truncate text-[12px] font-normal text-[#84828e]">
                            Product
                          </span>
                        </span>
                        <span className="truncate text-[13px] font-normal text-[#5f5a66]">
                          {productId}
                        </span>
                        <span className="text-[13px] font-normal text-[#5f5a66]">
                          {typeof product.active === "boolean"
                            ? String(product.active)
                            : "-"}
                        </span>
                        <span className="text-[13px] font-normal text-[#5f5a66]">
                          {`${prices.length} price${
                            prices.length === 1 ? "" : "s"
                          }`}
                        </span>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-0">
                      <div className="mb-4 grid grid-cols-[120px_1fr] gap-3 rounded-md bg-[#fbfafc] p-3 text-[13px]">
                        <div className="font-medium text-[#5f5a66]">
                          Metadata
                        </div>
                        <div className="break-all text-[#211f26]">
                          {product.metadata
                            ? JSON.stringify(product.metadata)
                            : "-"}
                        </div>
                      </div>

                      {prices.length > 0 ? (
                        <Table className="w-full">
                          <TableHeader>
                            <TableRow className="border-b-0">
                              <TableHead className="bg-[#faf9fb]">
                                Price
                              </TableHead>
                              <TableHead className="bg-[#faf9fb]">
                                Price ID
                              </TableHead>
                              <TableHead className="bg-[#faf9fb]">
                                Active
                              </TableHead>
                              <TableHead className="bg-[#faf9fb]">
                                Amount
                              </TableHead>
                              <TableHead className="bg-[#faf9fb]">
                                Metadata
                              </TableHead>
                            </TableRow>
                          </TableHeader>

                          <TableBody>
                            {prices.map((price: any, priceIndex: number) => (
                              <TableRow
                                key={
                                  price.id || `${product.id}-${priceIndex}`
                                }
                              >
                                <TableCell>{getPriceName(price)}</TableCell>
                                <TableCell>
                                  {price.id || price.priceId || "-"}
                                </TableCell>
                                <TableCell>
                                  {typeof price.active === "boolean"
                                    ? String(price.active)
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  {formatAmount(
                                    price.unit_amount,
                                    price.currency,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {price.metadata
                                    ? JSON.stringify(price.metadata)
                                    : "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-[14px] text-[#84828e]">
                          No prices found.
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {products.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No products found.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
